import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    document.dispatchEvent(new CustomEvent('quick-add:modal-opened'));
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}




document.addEventListener('DOMContentLoaded', () => {
  let activeForm = null;
  let activeButton = null;
  let activeQuickAddComponent = null;
  const originalFetch = window.fetch;

  const getQtyWrap = (scope) => scope?.querySelector('.quick-add__qty');
  const getAddButton = (scope) => scope?.querySelector('.quick-add__button--add');
  const getChooseButton = (scope) => scope?.querySelector('.quick-add__button--choose');
  const getChooseLabels = (scope) => Array.from(scope?.querySelectorAll('.quick-add__choose-label') || []);
  const getChooseCount = (scope) => scope?.querySelector('.quick-add__choose-count');

  const setChooseButtonState = (component, totalQty, options = {}) => {
    if (!component) return;
    const chooseButton = getChooseButton(component);
    const chooseLabels = getChooseLabels(component);
    const chooseCount = getChooseCount(component);
    if (!chooseButton || !chooseLabels.length) return;

    const baseLabel = chooseLabels[0]?.dataset.baseLabel || 'Add';
    const nextQty = Number(totalQty || 0);
    const existingQty = Number(component.dataset.lastKnownProductQty || chooseCount?.textContent || 0);

    if (nextQty > 0) {
      component.dataset.lastKnownProductQty = String(nextQty);
      chooseLabels.forEach((label) => {
        label.textContent = String(nextQty);
      });
      chooseButton.classList.add('has-items');
      if (chooseCount) {
        chooseCount.textContent = String(nextQty);
        chooseCount.classList.add('hidden');
      }
    } else if (!options.preserveExisting) {
      if (existingQty > 0 && options.allowZeroReset !== true) {
        chooseLabels.forEach((label) => {
          label.textContent = String(existingQty);
        });
        chooseButton.classList.add('has-items');
        if (chooseCount) {
          chooseCount.textContent = String(existingQty);
          chooseCount.classList.add('hidden');
        }
        return;
      }
      component.dataset.lastKnownProductQty = '0';
      chooseLabels.forEach((label) => {
        label.textContent = baseLabel;
      });
      chooseButton.classList.remove('has-items');
      if (chooseCount) {
        chooseCount.textContent = '0';
        chooseCount.classList.add('hidden');
      }
    }
  };

  const setSingleVariantState = (component, variantQty, options = {}) => {
    if (!component) return;
    const qtyWrap = getQtyWrap(component);
    const addButton = getAddButton(component);
    if (!qtyWrap || !addButton) return;

    const nextQty = Number(variantQty || 0);
    const existingQty = Number(component.dataset.lastKnownVariantQty || qtyWrap.querySelector('.quick-add__qty-value')?.textContent || 0);
    const valueEl = qtyWrap.querySelector('.quick-add__qty-value');

    if (nextQty > 0) {
      component.dataset.lastKnownVariantQty = String(nextQty);
      if (valueEl) valueEl.textContent = String(nextQty);
      qtyWrap.classList.remove('hidden');
      addButton.classList.add('hidden');
      qtyWrap.style.opacity = '1';
      qtyWrap.style.visibility = 'visible';
    } else if (!options.preserveExisting) {
      if (existingQty > 0 && options.allowZeroReset !== true) {
        component.dataset.lastKnownVariantQty = String(existingQty);
        if (valueEl) valueEl.textContent = String(existingQty);
        qtyWrap.classList.remove('hidden');
        addButton.classList.add('hidden');
        qtyWrap.style.opacity = '1';
        qtyWrap.style.visibility = 'visible';
        return;
      }
      component.dataset.lastKnownVariantQty = '0';
      if (valueEl) valueEl.textContent = '1';
      qtyWrap.classList.add('hidden');
      addButton.classList.remove('hidden');
    }
  };

  const markPendingQuickAdd = (component) => {
    if (!component) return;
    component.dataset.pendingCartSync = 'true';
    const existingQty = Number(component.dataset.lastKnownProductQty || component.dataset.lastKnownVariantQty || 0);
    if (component.dataset.quickAddButton === 'add') {
      setSingleVariantState(component, Math.max(existingQty, 1), { preserveExisting: true });
    } else {
      setChooseButtonState(component, Math.max(existingQty, 1), { preserveExisting: true });
    }
  };

  const clearPendingQuickAdd = (component) => {
    if (!component) return;
    delete component.dataset.pendingCartSync;
  };

  let cartRequest = null;
  let latestCart = window.__akCartState?.items ? window.__akCartState : null;

  const isFullCartResponse = (cart) => Boolean(cart && Array.isArray(cart.items));

  const setLatestCart = (cart) => {
    if (isFullCartResponse(cart)) {
      latestCart = cart;
      window.__akCartState = cart;
    }
    return latestCart;
  };

  const getCart = async (forceRefresh = false) => {
    if (!forceRefresh && latestCart) return latestCart;
    if (cartRequest) return cartRequest;

    cartRequest = originalFetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((response) => response.json())
      .then((cart) => setLatestCart(cart) || cart)
      .finally(() => {
        cartRequest = null;
      });

    return cartRequest;
  };

  const getVariantQuantity = (cart, variantId) =>
    cart.items
      .filter((item) => String(item.variant_id) === String(variantId))
      .reduce((total, item) => total + item.quantity, 0);

  const getVariantLineItem = (cart, variantId) =>
    cart.items.find((item) => String(item.variant_id) === String(variantId)) || null;

  const getProductQuantity = (cart, productId) =>
    cart.items
      .filter((item) => String(item.product_id) === String(productId))
      .reduce((total, item) => total + item.quantity, 0);

  const syncQuantitySelectorInput = (quantitySelector, cartQty) => {
    if (!quantitySelector) return;

    const input = quantitySelector.querySelector('input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return;

    const min = Number(input.getAttribute('data-min') || input.min || 1) || 1;
    const nextValue = cartQty > 0 ? cartQty : min;

    input.setAttribute('data-cart-quantity', String(cartQty));
    input.defaultValue = String(nextValue);
    input.value = String(nextValue);

    if (typeof quantitySelector.setCartQuantity === 'function') {
      quantitySelector.setCartQuantity(cartQty);
    }
    if (typeof quantitySelector.setValue === 'function') {
      quantitySelector.setValue(String(nextValue));
    }
    if (typeof quantitySelector.updateButtonStates === 'function') {
      quantitySelector.updateButtonStates();
    }
  };

  const syncQuickAddModal = (cart = latestCart) => {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent || !cart) return;

    const variantId = modalContent.querySelector('product-form-component input[name="id"]')?.value;
    const quantitySelector = modalContent.querySelector('quantity-selector-component');
    if (!variantId || !quantitySelector) return;

    const lineItem = getVariantLineItem(cart, variantId);
    syncQuantitySelectorInput(quantitySelector, Number(lineItem?.quantity || 0));
  };

  const syncQuickAddComponent = (component, cart) => {
    const qtyWrap = getQtyWrap(component);
    const addButton = getAddButton(component);
    const chooseButton = getChooseButton(component);
    const chooseLabels = getChooseLabels(component);
    const chooseCount = getChooseCount(component);
    const productId = component.dataset.productId;

    if (chooseButton && chooseLabels.length && productId) {
      const totalProductQty = getProductQuantity(cart, productId);
      const pendingQty = Number(component.dataset.lastKnownProductQty || 0);
      const shouldPreserve = component.dataset.pendingCartSync === 'true' && totalProductQty === 0 && pendingQty > 0;
      setChooseButtonState(component, shouldPreserve ? pendingQty : totalProductQty, {
        preserveExisting: shouldPreserve,
        allowZeroReset: !shouldPreserve,
      });
    }

    if (qtyWrap && addButton) {
      const variantId = qtyWrap.dataset.variantId;
      const currentButtonMode = component.dataset.quickAddButton;

      if (variantId && currentButtonMode === 'add') {
        const variantQty = getVariantQuantity(cart, variantId);
        const pendingQty = Number(component.dataset.lastKnownVariantQty || 0);
        const shouldPreserve = component.dataset.pendingCartSync === 'true' && variantQty === 0 && pendingQty > 0;
        setSingleVariantState(component, shouldPreserve ? pendingQty : variantQty, {
          preserveExisting: shouldPreserve,
          allowZeroReset: !shouldPreserve,
        });
      }
    }

    if (component.dataset.pendingCartSync === 'true') {
      clearPendingQuickAdd(component);
    }
  };

  const syncMatchingQuickAdds = (cart, options = {}) => {
    if (!cart) return;

    const productId = options.productId != null ? String(options.productId) : null;
    const variantId = options.variantId != null ? String(options.variantId) : null;
    const activeComponent = options.activeComponent || null;
    const forceAll = options.forceAll === true;

    document.querySelectorAll('quick-add-component').forEach((component) => {
      if (activeComponent && component === activeComponent) {
        syncQuickAddComponent(component, cart);
        return;
      }

      const componentProductId = component.dataset.productId ? String(component.dataset.productId) : null;
      const componentVariantId = component.querySelector('.quick-add__qty')?.dataset.variantId
        ? String(component.querySelector('.quick-add__qty')?.dataset.variantId)
        : null;
      const lastKnownProductQty = Number(component.dataset.lastKnownProductQty || 0);
      const lastKnownVariantQty = Number(component.dataset.lastKnownVariantQty || 0);
      const hasVisibleQty = Boolean(component.querySelector('.quick-add__qty:not(.hidden)'));
      const isPending = component.dataset.pendingCartSync === 'true';

      const matchesProduct = productId && componentProductId === productId;
      const matchesVariant = variantId && componentVariantId === variantId;
      const shouldSync = forceAll || matchesProduct || matchesVariant || isPending || hasVisibleQty || lastKnownProductQty > 0 || lastKnownVariantQty > 0;

      if (shouldSync) {
        syncQuickAddComponent(component, cart);
      }
    });

    syncQuickAddModal(cart);
  };

  const syncAllQuickAdds = async (cartOverride = null, forceRefresh = false, forceAll = false) => {
    try {
      const cart = cartOverride || (await getCart(forceRefresh));
      syncMatchingQuickAdds(cart, { forceAll });
      return cart;
    } catch (error) {
      console.warn('Quick add sync failed:', error);
      return null;
    }
  };

  const broadcastCartUpdate = (cart, source = 'quick-add-component') => {
    if (!cart) return;
    setLatestCart(cart);
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        detail: {
          resource: cart,
          sourceId: 'quick-add-sync',
          data: {
            cart,
            source,
            itemCount: Number(cart.item_count || 0),
          },
        },
      })
    );
  };

  let hardSyncTimer = null;
  const scheduleHardSync = (delay = 40, forceRefresh = true) => {
    if (hardSyncTimer) window.clearTimeout(hardSyncTimer);
    hardSyncTimer = window.setTimeout(async () => {
      hardSyncTimer = null;
      try {
        const cart = await getCart(forceRefresh);
        syncMatchingQuickAdds(cart, { forceAll: true });
      } catch (error) {
        console.warn('Quick add hard sync failed:', error);
      }
    }, delay);
  };

  let restoreSyncTimer = null;
  const syncAfterReturnNavigation = async () => {
    try {
      const freshCart = await getCart(true);
      await syncAllQuickAdds(freshCart, false, true);
      broadcastCartUpdate(freshCart, 'page-restore');
    } catch (error) {
      console.warn('Quick add restore sync failed:', error);
    }
  };

  const scheduleRestoreSync = (delay = 0) => {
    if (restoreSyncTimer) window.clearTimeout(restoreSyncTimer);
    restoreSyncTimer = window.setTimeout(() => {
      restoreSyncTimer = null;
      syncAfterReturnNavigation();
    }, delay);
  };

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('.quick-add__button--add');
    if (addButton) {
      activeButton = addButton;
      activeQuickAddComponent = addButton.closest('quick-add-component');
      activeForm = addButton.closest('form') || addButton.closest('product-form-component')?.querySelector('form') || null;
      markPendingQuickAdd(activeQuickAddComponent);
      return;
    }

    const chooseButton = event.target.closest('.quick-add__button--choose');
    if (chooseButton) {
      activeQuickAddComponent = chooseButton.closest('quick-add-component');
      if (latestCart) {
        syncQuickAddModal(latestCart);
      } else {
        getCart(true).then((cart) => syncQuickAddModal(cart)).catch(() => {});
      }
      return;
    }
  });

  document.addEventListener('click', async (event) => {
    const qtyBtn = event.target.closest('.quick-add__qty-btn');
    if (!qtyBtn) return;

    const qtyWrap = qtyBtn.closest('.quick-add__qty');
    if (!qtyWrap || qtyWrap.classList.contains('is-updating')) return;

    const variantId = qtyWrap.dataset.variantId;
    const valueEl = qtyWrap.querySelector('.quick-add__qty-value');
    const action = qtyBtn.dataset.action;

    qtyWrap.classList.add('is-updating');
    try {
      const cart = await getCart(true);
      const lineItem = getVariantLineItem(cart, variantId);

      if (!lineItem) {
        qtyWrap.classList.add('hidden');
        getAddButton(qtyWrap.closest('quick-add-component'))?.classList.remove('hidden');
        await syncAllQuickAdds(null, true);
        return;
      }

      const currentQty = Number(lineItem.quantity || 0);
      let newQty = currentQty + (action === 'increase' ? 1 : -1);
      if (newQty < 0) newQty = 0;

      if (valueEl) valueEl.textContent = String(newQty > 0 ? newQty : 1);
      if (newQty === 0) {
        qtyWrap.classList.add('hidden');
        getAddButton(qtyWrap.closest('quick-add-component'))?.classList.remove('hidden');
      }

      const changeResponse = await originalFetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineItem.key, quantity: newQty }),
      });

      const updatedCart = changeResponse.ok ? await changeResponse.json() : await getCart(true);
      setLatestCart(updatedCart);
      await syncAllQuickAdds(updatedCart, false);
      broadcastCartUpdate(updatedCart);
    } catch (error) {
      console.warn('Quick add quantity update failed:', error);
      await syncAllQuickAdds(null, true);
    } finally {
      qtyWrap.classList.remove('is-updating');
    }
  });

  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);

    try {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
      const isCartMutation =
        url &&
        (url.includes('/cart/add') ||
          url.includes('/cart/change') ||
          url.includes('/cart/update') ||
          url.includes('/cart/clear'));

      if (isCartMutation && response.ok) {
        const activeComponent =
          activeQuickAddComponent || activeButton?.closest('quick-add-component') || activeForm?.closest('quick-add-component');

        if (activeComponent?.dataset.quickAddButton === 'add') {
          activeComponent.dataset.lastKnownVariantQty = activeComponent.dataset.lastKnownVariantQty || '1';
          const qtyWrap = getQtyWrap(activeComponent);
          activeButton?.classList.add('hidden');
          qtyWrap?.classList.remove('hidden');
          if (qtyWrap) {
            qtyWrap.style.opacity = '1';
            qtyWrap.style.visibility = 'visible';
          }
        }

        let cart = null;
        try {
          const data = await response.clone().json();
          if (isFullCartResponse(data)) {
            cart = data;
          }
        } catch (_error) {}

        if (!cart) {
          cart = await getCart(true);
        } else {
          setLatestCart(cart);
        }

        syncMatchingQuickAdds(cart, {
          activeComponent,
          productId: activeComponent?.dataset.productId || activeForm?.closest('product-form-component')?.dataset.productId || null,
          variantId: activeForm?.querySelector('input[name="id"]')?.value || null,
        });
        if (activeComponent) {
          clearPendingQuickAdd(activeComponent);
        }
        broadcastCartUpdate(cart);
        scheduleHardSync(60, true);
      }
    } catch (error) {
      console.warn('Quick add stepper setup failed:', error);
    } finally {
      activeForm = null;
      activeButton = null;
      activeQuickAddComponent = null;
    }

    return response;
  };

  document.addEventListener('cart:update', (event) => {
    const cart = event.detail?.resource || event.detail?.data?.cart || null;
    if (cart?.items) {
      setLatestCart(cart);
      syncMatchingQuickAdds(cart, { forceAll: true });
      scheduleHardSync(50, true);
    } else {
      scheduleHardSync(20, true);
    }
  });

  document.addEventListener('ak:product-list-appended', () => {
    syncAllQuickAdds(null, false, true);
  });

  document.addEventListener('quick-add:modal-opened', () => {
    if (latestCart) {
      syncQuickAddModal(latestCart);
    } else {
      getCart(true).then((cart) => syncQuickAddModal(cart)).catch(() => {});
    }
  });

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (!event.target.closest('quick-add-dialog')) return;
    window.requestAnimationFrame(() => {
      if (latestCart) {
        syncQuickAddModal(latestCart);
      } else {
        getCart(true).then((cart) => syncQuickAddModal(cart)).catch(() => {});
      }
    });
  });

  window.addEventListener('pageshow', (event) => {
    scheduleRestoreSync(event.persisted ? 30 : 0);
    if (event.persisted) {
      scheduleRestoreSync(220);
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      scheduleRestoreSync(30);
    }
  });

  window.addEventListener('focus', () => {
    scheduleRestoreSync(30);
  });

  syncAllQuickAdds(null, true, true);
});
